import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  Heart,
  Moon,
  Dumbbell,
  Apple,
  Droplets,
  Smile,
  Target,
} from 'lucide-react';
import { HealthMetrics } from '../types';

interface QuickStatsProps {
  healthMetrics: HealthMetrics;
}

const QuickStats: React.FC<QuickStatsProps> = ({ healthMetrics }) => {
  const metrics = [
    {
      label: 'Overall Score',
      value: healthMetrics.overallScore,
      icon: <Target />,
      color: '#00ff88',
      trend: 'up',
    },
    {
      label: 'Energy Level',
      value: healthMetrics.energyLevel,
      icon: <Zap />,
      color: '#ffd700',
      trend: 'up',
    },
    {
      label: 'Sleep Quality',
      value: healthMetrics.sleepQuality,
      icon: <Moon />,
      color: '#4fc3f7',
      trend: 'stable',
    },
    {
      label: 'Physical Activity',
      value: healthMetrics.physicalActivity,
      icon: <Dumbbell />,
      color: '#ff6b6b',
      trend: 'down',
    },
  ];

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp size={16} color="#00ff88" />;
      case 'down':
        return <TrendingDown size={16} color="#ff6b6b" />;
      default:
        return <Minus size={16} color="#ffd700" />;
    }
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
          Quick Stats
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {metrics.map((metric) => (
            <Box key={metric.label}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: metric.color }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {metric.label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTrendIcon(metric.trend)}
                  <Typography variant="h6" sx={{ color: metric.color, fontWeight: 600 }}>
                    {metric.value}%
                  </Typography>
                </Box>
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={metric.value}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: metric.color,
                    borderRadius: 3,
                  },
                }}
              />
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {getScoreLabel(metric.value)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Last Updated
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(healthMetrics.lastUpdated).toLocaleString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuickStats;
